import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Circus Daily Checkbox — Figma 8001:49010
 */
const checkboxVariants = cva(
  'peer shrink-0 rounded-[4px] border border-white bg-transparent ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-white/15 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-white',
  {
    variants: {
      size: {
        default: 'size-5',
        sm: 'size-4',
        lg: 'size-6',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  label?: React.ReactNode;
  layout?: 'horizontal' | 'vertical';
}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, size, label, layout = 'horizontal', id, disabled, ...props }, ref) => {
    const checkboxId = id ?? React.useId();

    const control = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        disabled={disabled}
        className={cn(checkboxVariants({ size }), className)}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
          <Check className="size-3.5 stroke-[3]" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );

    if (!label) return control;

    return (
      <label
        htmlFor={checkboxId}
        className={cn(
          'inline-flex gap-2',
          layout === 'vertical' ? 'flex-col items-center gap-1' : 'items-start',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        {control}
        <span
          className={cn(
            'text-sm font-sans',
            disabled ? 'text-white/40' : 'text-white',
            layout === 'horizontal' ? 'pt-0.5' : 'text-center'
          )}
        >
          {label}
        </span>
      </label>
    );
  }
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, checkboxVariants };
